/**
 * SchemaEditFormBody — shared form body for CREATE / ALTER SCHEMA.
 *
 * Engines:
 *   - mssql:    create-only (T-SQL has no schema rename — `alter-schema` is
 *               reserved for object transfer, covered by Slice D1's
 *               `move-table-schema`). Owner via `AUTHORIZATION user`.
 *   - postgres: create + rename. Owner via `AUTHORIZATION role`. Optional
 *               `IF NOT EXISTS` on create.
 *   - mysql / oracle / sqlite: unsupported affordance with documented
 *               redirect tooltip (database / user / ATTACH respectively).
 *
 * Modes:
 *   - `create`: builds a `SchemaDef` and dispatches `commitCreateSchema`.
 *   - `alter`:  builds a `SchemaChange` (rename) and dispatches
 *               `commitAlterSchema`. mssql renders the alter mode disabled
 *               with the "create + transfer + drop" redirect.
 *
 * The owning popover seeds `initialValues` from the parked statement.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D5 — Schema UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  commitAlterSchema,
  getAlterSchemaUnsupportedTooltip,
  isAlterSchemaSupported,
  validateAlterSchemaForEngine,
} from '../../edit/edits/alterSchema';
import {
  commitCreateSchema,
  getCreateSchemaUnsupportedTooltip,
  isCreateSchemaSupported,
  validateCreateSchemaForEngine,
} from '../../edit/edits/createSchema';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type SchemaEditMode = 'create' | 'alter';

export interface SchemaFormValues {
  readonly name: string;
  readonly authorization: string;
  readonly ifNotExists: boolean;
  readonly renameTo: string;
}

export interface SchemaEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: SchemaEditMode;
  readonly initialValues?: Partial<SchemaFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function SchemaEditFormBody(props: SchemaEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const supported =
    mode === 'create' ? isCreateSchemaSupported(engine) : isAlterSchemaSupported(engine);
  const unsupportedTooltip =
    mode === 'create'
      ? getCreateSchemaUnsupportedTooltip(engine)
      : getAlterSchemaUnsupportedTooltip(engine);

  const [values, setValues] = useState<SchemaFormValues>({
    name: initialValues?.name ?? '',
    authorization: initialValues?.authorization ?? '',
    ifNotExists: initialValues?.ifNotExists ?? false,
    renameTo: initialValues?.renameTo ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!supported) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create schema' : 'Edit schema'}
        </div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {unsupportedTooltip}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  const update = <K extends keyof SchemaFormValues>(
    key: K,
    value: SchemaFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const auth = values.authorization.trim();
      const schema = {
        name: values.name.trim(),
        ...(engine === 'postgres' && values.ifNotExists ? { ifNotExists: true } : {}),
        ...(auth.length > 0 && engine === 'mssql' ? { mssqlAuthorization: auth } : {}),
        ...(auth.length > 0 && engine === 'postgres' ? { pgsqlAuthorization: auth } : {}),
      };
      const validationError = validateCreateSchemaForEngine(engine, { schema });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreateSchema(editContext, { schema });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const args = {
      schemaName: values.name.trim(),
      change: { renameTo: values.renameTo.trim() },
    };
    const validationError = validateAlterSchemaForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterSchema(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create' ? 'Create schema' : 'Rename schema';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create schema' : 'Rename schema'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${testId}-name`} className="text-[11px]">
          {mode === 'create' ? 'Name' : 'Existing name'}
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          className="h-7 text-xs"
          value={values.name}
          onChange={(e) => update('name', e.target.value)}
        />
      </div>

      {mode === 'create' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${testId}-auth`} className="text-[11px]">
              Authorization (owner) — optional
            </Label>
            <Input
              id={`${testId}-auth`}
              data-testid={`${testId}-auth`}
              className="h-7 text-xs"
              value={values.authorization}
              onChange={(e) => update('authorization', e.target.value)}
            />
          </div>
          {engine === 'postgres' ? (
            <label
              className="flex items-center gap-2 text-[11px]"
              data-testid={`${testId}-if-not-exists-label`}
            >
              <Checkbox
                data-testid={`${testId}-if-not-exists`}
                checked={values.ifNotExists}
                onCheckedChange={(checked) => update('ifNotExists', checked === true)}
              />
              IF NOT EXISTS
            </label>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${testId}-rename-to`} className="text-[11px]">
            New name
          </Label>
          <Input
            id={`${testId}-rename-to`}
            data-testid={`${testId}-rename-to`}
            className="h-7 text-xs"
            value={values.renameTo}
            onChange={(e) => update('renameTo', e.target.value)}
          />
        </div>
      )}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}
